import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { midas } from '@resurs/midas'
import IconButton from '../../../../internal/IconButton'
import { getText } from '../../../../internal/TextLabels'
import { useOnMount, useToggle } from '../../../../util/utility-hooks'

const escapeRegex = regex =>
  regex
    ?.replace(/\[ \|-]/g, '[ \\-]') // phone numbers
    ?.replace(/\*\+\/=\?\^_`~-/g, '*+\\/=?^_`~\\-') // email

const FormTextInput = props => {
  const inputRef = useRef(false)
  const [dirty, toggleDirty] = useToggle()
  const [active, toggleActive] = useToggle()
  const [formatError, toggleFormatError] = useToggle()
  const [requiredError, toggleRequiredError] = useToggle()
  const [required, toggleRequired] = useToggle()
  const [additionalValidationError, setAdditionalValidationError] = useState('')
  const [blurred, toggleBlurred] = useToggle()
  const [valid, toggleValid] = useToggle()

  const [value, setValue] = useState(props.value)
  const [validityMessage, setValidityMessage] = useState('')

  const testValidity = inputField => {
    const { id, type, format } = props
    const validity = inputField.validity
    let isValid = validity.valid
    let _validityMessage = ''

    if (props.validityErrorFormatter) {
      _validityMessage = props.validityErrorFormatter(validity, props) || ''
    }

    // html5 validation does not prevent decimal input when type is number even if pattern only allows integers
    if (
      type === 'number' &&
      (format || {}).regex &&
      inputField.value &&
      !_validityMessage
    ) {
      _validityMessage = new RegExp(format.regex).test(inputField.value)
        ? ''
        : format.error
    }

    const _additionalValidationError =
      isValid && props.additionalValidation(inputField.value)

    isValid = isValid && !_additionalValidationError && !_validityMessage
    const validityChanged = isValid !== valid
    toggleValid(isValid)
    toggleFormatError(validity.patternMismatch)
    toggleRequiredError(validity.valueMissing)
    setAdditionalValidationError(_additionalValidationError)
    setValidityMessage(_validityMessage)
    if (props.isValid && validityChanged) {
      props.isValid({ key: id, valid: isValid })
    }
  }

  const modifyNumberInputValue = modifyFunc => {
    if (props.type !== 'number') {
      // eslint-disable-next-line no-console
      console.warn('not a number input')
      return
    }
    if (props.disabled || !modifyFunc) {
      return
    }
    modifyFunc()
    inputRef.current.dispatchEvent(new Event('input', { bubbles: true }))
  }

  useOnMount(() => testValidity(inputRef.current))
  useEffect(() => toggleRequired(props.required), [props.required])
  useEffect(() => {
    setValue(props.value)
    testValidity(inputRef.current)
  }, [props.value])

  const test = e => {
    toggleDirty(true)
    testValidity(e.target)
  }

  const handleInvalid = e => {
    e.preventDefault()
    test(e)
  }

  const handleChange = e => {
    setValue(e.target.value)
    props.onChange(e)
  }

  const {
    children,
    disabled,
    readonly,
    id,
    label,
    placeholder,
    type,
    hint,
    size,
    skipValidation,
    disableAutocomplete,
    autoFocus,
    format,
    onClick,
    dataCy,
    min,
    max,
    stepControls,
  } = props

  const textInputProps = {
    id,
    name: id,
    placeholder,
    readOnly: readonly,
    disabled,
    required: props.required,
    type,
    min,
    max,
    value,
    pattern: format && format.regex ? escapeRegex(format.regex) : undefined,
    autoComplete: disableAutocomplete ? 'off' : undefined,
    autoFocus: autoFocus ? 'autofocus' : undefined,
    style: stepControls ? { textAlign: 'center' } : {},
    onChange: e => {
      if (!disabled) {
        handleChange(e)
      }
    },
    onClick: e => {
      if (!disabled) {
        onClick(e)
      }
    },
    onKeyUp: e => {
      if (!skipValidation) {
        test(e)
      }
    },
    onTouchEnd: e => {
      if (!skipValidation) {
        setTimeout(() => test(e), 100)
      }
    },
    onFocus: () => {
      if (props.valid) {
        toggleValid(true)
        toggleDirty(true)
      }
      toggleActive(true)
    },
    onBlur: e => {
      toggleActive(false)
      toggleBlurred(true)
      if (!skipValidation) {
        test(e)
      }
    },
    onInvalid: e => {
      if (!disabled) {
        handleInvalid(e)
      }
    },
  }

  const wrapperClassNames = [
    midas.formGroup,
    dirty && !valid && required && requiredError && midas.hasWarning,
    dirty && !valid && !active && midas.hasDanger,
    dirty && valid && value !== '' && midas.hasSuccess,
    dirty && props.valid && value !== '' && midas.hasSuccess,
    additionalValidationError && midas.hasDanger,
    validityMessage && midas.hasDanger,
  ]
    .filter(val => val)
    .join(' ')

  const textInputClassNames = [
    midas.formInput,
    size === 'small' && midas.inputSm,
    size === 'large' && midas.inputLg,
    size === 'xlarge' && midas.inputXl,
  ]
    .filter(val => val)
    .join(' ')

  const labelText =
    label && type !== 'hidden' ? (
      <label className={midas.formLabel} htmlFor={id}>
        {label} {props.required && <span>*</span>}
      </label>
    ) : null

  const hintValue = hint ? (
    <span className={midas.formInputHint}>{hint}</span>
  ) : null

  const errorValue = dirty &&
    !valid &&
    !active &&
    formatError &&
    !requiredError && (
      <span className={midas.formInputHint}>{format.error}</span>
    )

  const additionalErrorValue = additionalValidationError && (
    <span className={midas.formInputHint}>{additionalValidationError}</span>
  )

  const requiredValue = dirty && !active && required && requiredError && (
    <span className={midas.formInputHint}>{props.required}</span>
  )

  const _validityMessage = !errorValue &&
    !additionalErrorValue &&
    !requiredValue &&
    validityMessage &&
    blurred && <span className={midas.formInputHint}>{validityMessage}</span>

  const input = (
    <input ref={inputRef} className={textInputClassNames} {...textInputProps} />
  )

  const wrappedInput = !stepControls ? (
    input
  ) : (
    <div style={{ display: 'flex' }}>
      <IconButton
        icon="midas-minus-1"
        blue
        border
        stretched
        disabled={value <= min}
        onClick={() => {
          modifyNumberInputValue(() => inputRef.current.stepDown())
        }}
      />
      <div style={{ width: '75px', margin: '0 6px', textAlign: 'center' }}>
        {input}
      </div>
      <IconButton
        icon="midas-add-1"
        blue
        border
        stretched
        disabled={value >= max}
        onClick={() => {
          modifyNumberInputValue(() => inputRef.current.stepUp())
        }}
      />
    </div>
  )

  return (
    <div className={wrapperClassNames} data-cy={dataCy}>
      {labelText}
      {children ? (
        <div className={midas.inputGroup}>
          {wrappedInput}
          {children}
        </div>
      ) : (
        wrappedInput
      )}
      {hintValue}
      {errorValue}
      {requiredValue}
      {additionalErrorValue}
      {_validityMessage}
    </div>
  )
}

FormTextInput.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  size: PropTypes.oneOf(['small', 'normal', 'large', 'xlarge']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valid: PropTypes.bool,
  required: PropTypes.string,
  format: props => {
    if (!props.required) {
      if (!props.skipValidation && !props.format) {
        return new Error(
          'Midas Error: Text Input requires a format, otherwise skipValidation must be true',
        )
      }
      return undefined
    }
    return undefined
  },
  validityErrorFormatter: PropTypes.func,
  disableAutocomplete: PropTypes.bool,
  isValid: PropTypes.func,
  hint: PropTypes.string,
  skipValidation: PropTypes.bool,
  autoFocus: PropTypes.bool,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  additionalValidation: PropTypes.func,
  dataCy: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  stepControls: PropTypes.bool,
}
FormTextInput.defaultProps = {
  children: null,
  disabled: false,
  readonly: false,
  placeholder: null,
  value: '',
  type: 'text',
  size: 'normal',
  valid: false,
  required: null,
  format: null,
  skipValidation: false,
  label: null,
  hint: null,
  disableAutocomplete: false,
  autoFocus: false,
  dataCy: null,
  min: null,
  max: null,
  stepControls: false,
  isValid: () => {},
  onChange: () => {},
  onClick: () => {},
  additionalValidation: () => {},
  validityErrorFormatter: (validity, props) => {
    let errorType = ''
    let args = []
    switch (true) {
      case validity.valid:
        return ''
      case validity.valueMissing:
        return ''
      case validity.customError:
        return props.customError
      case validity.rangeOverflow:
        errorType = 'rangeOverflow'
        args = [props.max]
        break
      case validity.rangeUnderflow:
        errorType = 'rangeUnderflow'
        args = [props.min]
        break
      case validity.patternMismatch:
      case validity.stepMismatch:
      case validity.tooLong:
      case validity.typeMismatch:
      default:
        errorType = 'patternMismatch'
        break
    }
    return getText(`default.form.formatError.${errorType}`, ...args)
  },
}

export default FormTextInput
